import {Component, DestroyRef, OnInit, computed, inject, signal} from '@angular/core';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {Router} from '@angular/router';
import {WinOlsStatusFilter, WinOlsTransactionService} from '../../../service/winols-transaction.service';
import {DashboardPeriodService} from '../../services/dashboard-period.service';

interface StatusSegment {
  status: WinOlsStatusFilter;
  label: string;
  count: number;
  color: string;
}

@Component({
  selector: 'app-winols-transactions-card',
  templateUrl: './winols-transactions-card.component.html',
  styleUrls: ['./winols-transactions-card.component.css']
})
export class WinolsTransactionsCardComponent implements OnInit {

  private readonly winOlsService = inject(WinOlsTransactionService);
  private readonly periodService = inject(DashboardPeriodService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly rangeChanges = toObservable(this.periodService.currentRange);

  readonly isLoading = signal<boolean>(true);

  // Read directly off the service signals
  readonly preCheck = this.winOlsService.preCheck;
  readonly requested = this.winOlsService.requested;
  readonly solutionOffered = this.winOlsService.solutionOffered;
  readonly noAvailableOptions = this.winOlsService.noAvailableOptions;
  readonly completed = this.winOlsService.completed;
  readonly errorCount = this.winOlsService.error;

  readonly total = computed(() =>
    (this.preCheck() ?? 0) +
    (this.requested() ?? 0) +
    (this.solutionOffered() ?? 0) +
    (this.noAvailableOptions() ?? 0) +
    (this.completed() ?? 0) +
    (this.errorCount() ?? 0)
  );

  readonly segments = computed<StatusSegment[]>(() => [
    {status: 'SOLUTION_OFFERED', label: 'Solution Offered', count: this.solutionOffered() ?? 0, color: '#7C5CFC'},
    {status: 'COMPLETED', label: 'Completed',       count: this.completed() ?? 0,       color: '#8AF0A8'},
    {status: 'PRE_CHECK',  label: 'Precheck',        count: this.preCheck() ?? 0,        color: '#E8A838'},
    {status: 'REQUESTED', label: 'Requested',       count: this.requested() ?? 0,       color: '#5BB6FF'},
    {status: 'ERROR_OR_NO_OPTIONS',     label: 'Error / No Options', count: ((this.errorCount() ?? 0) + (this.noAvailableOptions() ?? 0)), color: '#FF6B8B'}
  ]);

  ngOnInit(): void {
    this.rangeChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load());
  }

  private async load(): Promise<void> {
    this.isLoading.set(true);
    const startStr = this.periodService.currentStart();
    const endStr = this.periodService.currentEnd();
    try {
      await this.winOlsService.getWinOlsStatistics(startStr, endStr);
    } catch {
      // swallow — isLoading clears in finally
    } finally {
      this.isLoading.set(false);
    }
  }

  segmentPct(count: number): number {
    const tot = this.total();
    if (!tot) return 0;
    return Math.round((count / tot) * 1000) / 10;
  }

  openTransactions(status: WinOlsStatusFilter): void {
    if (!this.segments().some(segment => segment.status === status && segment.count > 0)) return;

    this.router.navigate(['/app/win-ols-transactions'], {
      queryParams: {
        startDate: this.periodService.currentStart(),
        endDate: this.periodService.currentEnd(),
        status
      }
    });
  }
}
